<template>
    <div class="md-layout-item circle-tab-root">
        <div class="desktop-tabs">
            <md-tabs md-dynamic-height>
                <!-- DETAILS TAB START -->
                <md-tab id="tab-details" md-label="Details">
                    <md-field class="mb-0">
                        <label class="md-list-item-text circle-label">Circle Name</label>
                        <md-input v-model="circle.name" :name="`circle_name-${idx}`" v-validate="'required'"
                            data-vv-as="Circle Name" placeholder="Circle Name" :disabled="!circle.editing"
                            ref="nameInput-{{ circle.short }}" />
                    </md-field>
                    <p class="error error-font" v-show="errors.has(`circle_name-${idx}`)">
                        {{ errors.first(`circle_name-${idx}`) }}</p>
                    <md-field class="md-field md-theme-default md-disabled md-required md-has-value md-has-placeholder">
                        <label class="md-list-item-text circle-label">Circle Code</label>
                        <span class="md-input">{{ circle.short }}</span>
                    </md-field>
                    <md-field class="md-field md-theme-default md-disabled md-required md-has-value md-has-placeholder">
                        <label class="md-list-item-text circle-label">Circle Page URL</label>
                        <md-input v-model="circle.circlePageUrl" :name="`circle_page_url-${idx}`"
                            v-validate="{ required: true, regex: /^http(s?)\:\/\/[0-9a-zA-Z]([-.\w]*[0-9a-zA-Z])*(:(0-9)*)*(\/?)([a-zA-Z0-9\-\.\?\,\'\/\\\+&amp;%\$#_]*)?$/ }"
                            data-vv-as="Circle Page URL" placeholder="https://xxx.protopia.co" :disabled="!circle.editing"
                            @input="circle.circlePageUrl = ensureHttpsScheme(sanitizeUrl($event))" />
                    </md-field>
                    <p class="error error-font" v-show="errors.has(`circle_page_url-${idx}`)">
                        {{ errors.first(`circle_page_url-${idx}`) }}</p>
                    <md-field class="md-field md-theme-default md-disabled md-required md-has-value md-has-placeholder">
                        <label class="md-list-item-text circle-label">Action Page</label>
                        <md-select v-model="circle.actionPage" :disabled="!circle.editing">
                            <md-option :value="null" disabled>Select an option</md-option>
                            <md-option v-for="page in actionPageOptions" :key="page.code" :value="page.code">{{
                                page.name
                            }}</md-option>
                        </md-select>
                    </md-field>
                    <md-field class="md-field md-theme-default md-disabled md-required md-has-value md-has-placeholder">
                        <label class="md-list-item-text circle-label">Administrators</label>
                        <md-input v-model="circle.administrators" name="circle administrators"
                            placeholder="Add Administrators" :disabled="!circle.editing">
                        </md-input>
                    </md-field>
                    <md-field
                        class="no-underline md-field md-theme-default md-disabled md-required md-has-value md-has-placeholder">
                        <label class="md-list-item-text circle-label">Affinities</label>
                        <md-chips :md-static="!circle.editing" :md-clickable="circle.editing" v-model="circle.interests"
                            placeholder="Add Affinities..."></md-chips>
                    </md-field>
                </md-tab>
                <!-- DETAILS TAB END -->
                <!-- SUBJECTS TAB START -->
                <md-tab id="tab-subject" md-label="Subjects">
                    <div class="scrollable-tab">
                        <md-button class="add-btn" @click="openAddDialog('subject')" :disabled="!circle.editing">
                            + Add {{ this.wizardLabels.subject }}
                        </md-button>
                        <md-field v-for="(subject, sIdx) in circle.subjects" v-bind:key="`subject-${sIdx}`">
                            <label class="md-list-item-text circle-label">Subject {{ sIdx + 1
                            }}</label>
                            <md-input v-model="circle.subjects[sIdx]" name="wizard_subjects"
                                @input="circle.subjects[sIdx] = sanitizeCustomizations(circle.subjects[sIdx])"
                                placeholder="Add Subject" :disabled="!circle.editing" :md-counter="limits.subjects.max" :maxlength="limits.subjects.max"></md-input>
                            <md-button class="remove-btn" iconable @click="removeItem('subject', sIdx)"
                                :disabled="!circle.editing">
                                <i class="fa fa-trash small-icon" aria-hidden="true"></i>
                                <md-tooltip class="tooltip-text" md-direction="top">Remove Subject</md-tooltip>
                            </md-button>
                        </md-field>
                    </div>
                </md-tab>
                <!-- SUBJECTS TAB END -->
                <!-- INTRO TAB START -->
                <md-tab id="tab-intros" md-label="Introductions">
                    <div class="scrollable-tab">
                        <md-button class="add-btn" @click="openAddDialog('intro')" :disabled="!circle.editing">
                            + Add {{ this.wizardLabels.intro }}
                        </md-button>
                        <md-field v-for="(intro, iIdx) in circle.intros" v-bind:key="`intro-${iIdx}`">
                            <label class="md-list-item-text circle-label">Introduction {{ iIdx + 1
                            }}</label>
                            <md-textarea v-model="circle.intros[iIdx]" name="wizard_intros" placeholder="Add Intro"
                                @input="circle.intros[iIdx] = sanitizeCustomizations(circle.intros[iIdx])"
                                :disabled="!circle.editing" :md-counter="limits.intros.max" :maxlength="limits.intros.max"></md-textarea>
                            <md-button class="remove-btn" iconable @click="removeItem('intro', iIdx)"
                                :disabled="!circle.editing">
                                <i class="fa fa-trash small-icon" aria-hidden="true"></i>
                                <md-tooltip class="tooltip-text" md-direction="top">Remove Introduction</md-tooltip>
                            </md-button>
                        </md-field>
                    </div>
                </md-tab>
                <!-- INTRO TAB END -->
                <!-- QUESTIONS TAB START -->
                <md-tab id="tab-questions" md-label="Questions">
                    <div class="scrollable-tab">
                        <md-button class="add-btn" @click="openAddDialog('question')" :disabled="!circle.editing">
                            + Add {{ this.wizardLabels.question }}
                        </md-button>
                        <md-field v-for="(question, qIdx) in circle.questions" v-bind:key="`question-${qIdx}`">
                            <label class="md-list-item-text circle-label">Question {{ qIdx + 1
                            }}</label>
                            <md-textarea v-model="circle.questions[qIdx]" name="wizard_questions"
                                placeholder="Add question" :disabled="!circle.editing" :md-counter="limits.questions.max" :maxlength="limits.questions.max"></md-textarea>
                            <md-button class="remove-btn" iconable @click="removeItem('question', qIdx)"
                                :disabled="!circle.editing">
                                <i class="fa fa-trash small-icon" aria-hidden="true"></i>
                                <md-tooltip class="tooltip-text" md-direction="top">Remove Question</md-tooltip>
                            </md-button>
                        </md-field>
                    </div>
                </md-tab>
                <!-- QUESTIONS TAB END -->
                <!-- THANK YOU TAB START -->
                <md-tab id="tab-thankyou" md-label="Thank you">
                    <div class="scrollable-tab">
                        <md-button class="add-btn" @click="openAddDialog('thankyou')" :disabled="!circle.editing">
                            + Add {{ this.wizardLabels.thankyou }}
                        </md-button>
                        <md-field v-for="(thanks, tIdx) in circle.thankyou" v-bind:key="`thanks-${tIdx}`">
                            <label class="md-list-item-text circle-label">Thank you {{ tIdx + 1
                            }}</label>
                            <md-textarea v-model="circle.thankyou[tIdx]" name="wizard_thankyou"
                                placeholder="Add thank you" :disabled="!circle.editing" :md-counter="limits.thankyou.max" :maxlength="limits.thankyou.max"></md-textarea>
                            <md-button class="remove-btn" iconable @click="removeItem('thankyou', tIdx)"
                                :disabled="!circle.editing">
                                <i class="fa fa-trash small-icon" aria-hidden="true"></i>
                                <md-tooltip class="tooltip-text" md-direction="top">Remove Thank You</md-tooltip>
                            </md-button>
                        </md-field>
                    </div>
                </md-tab>
                <!-- THANK YOU TAB START -->
                <!-- PROMPT TAB START -->
                <md-tab id="tab-prompts" md-label="Prompts">
                    <div class="scrollable-tab">
                        <md-button class="add-btn" @click="openAddDialog('prompt')" :disabled="!circle.editing">
                            + Add {{ this.wizardLabels.prompt }}
                        </md-button>
                        <md-field v-for="(prompt, pIdx) in circle.prompts" v-bind:key="`prompt-${pIdx}`">
                            <label class="md-list-item-text circle-label">Prompt {{ pIdx + 1
                            }}</label>
                            <md-textarea v-model="circle.prompts[pIdx]" name="wizard_prompts" placeholder="Add prompt"
                                :disabled="!circle.editing" :md-counter="limits.prompts.max" :maxlength="limits.prompts.max"></md-textarea>
                            <md-button class="remove-btn" iconable @click="removeItem('prompt', pIdx)"
                                :disabled="!circle.editing">
                                <i class="fa fa-trash small-icon" aria-hidden="true"></i>
                                <md-tooltip class="tooltip-text" md-direction="top">Remove Prompt</md-tooltip>
                            </md-button>
                        </md-field>
                    </div>
                </md-tab>
                <!-- PROMPT TAB END -->
            </md-tabs>
        </div>
        <div class="mobile-tabs">
            <md-list :md-expand-single="true">
                <md-list-item md-expand md-expanded>
                    <div class="md-list-item-text">Details</div>
                    <div slot="md-expand" class="item-list-text">
                        <!-- copy exactly the “Details” content from above -->
                        <md-field class="mb-0">
                            <label class="md-list-item-text circle-label">Circle Name</label>
                            <md-input v-model="circle.name" :name="`circle_name-${idx}`" v-validate="'required'"
                                data-vv-as="Circle Name" placeholder="Circle Name" :disabled="!circle.editing"
                                ref="nameInput-{{ circle.short }}" />
                        </md-field>
                        <p class="error error-font" v-show="errors.has(`circle_name-${idx}`)">
                            {{ errors.first(`circle_name-${idx}`) }}
                        </p>

                        <md-field>
                            <label class="md-list-item-text circle-label">Circle Code</label>
                            <span class="md-input">{{ circle.short }}</span>
                        </md-field>

                        <md-field :class="{ 'md-has-value': !!circle.circlePageUrl }">
                            <label class="md-list-item-text circle-label">Circle Page URL</label>
                            <md-input v-model="circle.circlePageUrl" :name="`circle_page_url-${idx}`"
                                v-validate="{ required: true, regex: /^http(s?)\:\/\/[0-9a-zA-Z]([-.\w]*[0-9a-zA-Z])*(:(0-9)*)*(\/?)([a-zA-Z0-9\-\.\?\,\'\/\\\+&amp;%\$#_]*)?$/ }"
                                data-vv-as="Circle Page URL" placeholder="https://xxx.protopia.co" :disabled="!circle.editing"
                                @input="circle.circlePageUrl = ensureHttpsScheme(sanitizeUrl($event))" />
                        </md-field>
                        <p class="error error-font" v-show="errors.has(`circle_page_url-${idx}`)">
                            {{ errors.first(`circle_page_url-${idx}`) }}
                        </p>

                        <md-field>
                            <label class="md-list-item-text circle-label">Action Page</label>
                            <md-select v-model="circle.actionPage" :disabled="!circle.editing">
                                <md-option :value="null" disabled>Select an option</md-option>
                                <md-option v-for="page in actionPageOptions" :key="page.code" :value="page.code">
                                    {{ page.name }}
                                </md-option>
                            </md-select>
                        </md-field>

                        <md-field>
                            <label class="md-list-item-text circle-label">Administrators</label>
                            <md-input v-model="circle.administrators" name="circle administrators"
                                placeholder="Add Administrators" :disabled="!circle.editing" />
                        </md-field>

                        <md-field class="no-underline">
                            <label class="md-list-item-text circle-label">Affinities</label>
                            <md-chips :md-static="!circle.editing" :md-clickable="circle.editing"
                                v-model="circle.interests" placeholder="Add Affinities..." />
                        </md-field>
                    </div>
                </md-list-item>
                <!-- SUBJECTS SECTION -->
                <md-list-item md-expand>
                    <div class="md-list-item-text">Subjects</div>
                    <div slot="md-expand" class="item-list-text">
                        <div class="scrollable-tab">
                            <md-button class="add-btn" @click="openAddDialog('subject')" :disabled="!circle.editing">
                                + Add {{ wizardLabels.subject }}
                            </md-button>

                            <md-field v-for="(subject, sIdx) in circle.subjects" :key="`subject-${sIdx}`">
                                <label class="md-list-item-text circle-label">
                                    Subject {{ sIdx + 1 }}
                                </label>
                                <md-input v-model="circle.subjects[sIdx]" name="wizard_subjects" @input="
                                    circle.subjects[sIdx] = sanitizeCustomizations(
                                        circle.subjects[sIdx]
                                    )
                                    " placeholder="Add Subject" :disabled="!circle.editing" :md-counter="limits.subjects.max" :maxlength="limits.subjects.max"/>
                                <md-button class="remove-btn" iconable @click="removeItem('subject', sIdx)"
                                    :disabled="!circle.editing">
                                    <i class="fa fa-trash small-icon" aria-hidden="true"></i>
                                    <md-tooltip class="tooltip-text" md-direction="top">
                                        Remove Subject
                                    </md-tooltip>
                                </md-button>
                            </md-field>
                        </div>
                    </div>
                </md-list-item>

                <!-- INTRODUCTIONS SECTION -->
                <md-list-item md-expand>
                    <div class="md-list-item-text">Introductions</div>
                    <div slot="md-expand" class="item-list-text">
                        <div class="scrollable-tab">
                            <md-button class="add-btn" @click="openAddDialog('intro')" :disabled="!circle.editing">
                                + Add {{ wizardLabels.intro }}
                            </md-button>
                            <md-field v-for="(intro, iIdx) in circle.intros" :key="`intro-${iIdx}`">
                                <label class="md-list-item-text circle-label">
                                    Introduction {{ iIdx + 1 }}
                                </label>
                                <md-textarea v-model="circle.intros[iIdx]" name="wizard_intros" placeholder="Add Intro"
                                    @input="
                                        circle.intros[iIdx] = sanitizeCustomizations(
                                            circle.intros[iIdx]
                                        )
                                        " :disabled="!circle.editing" :md-counter="limits.intros.max" :maxlength="limits.intros.max"/>
                                <md-button class="remove-btn" iconable @click="removeItem('intro', iIdx)"
                                    :disabled="!circle.editing">
                                    <i class="fa fa-trash small-icon" aria-hidden="true"></i>
                                    <md-tooltip class="tooltip-text" md-direction="top">
                                        Remove Introduction
                                    </md-tooltip>
                                </md-button>
                            </md-field>
                        </div>
                    </div>
                </md-list-item>

                <!-- QUESTIONS SECTION -->
                <md-list-item md-expand>
                    <div class="md-list-item-text">Questions</div>
                    <div slot="md-expand" class="item-list-text">
                        <div class="scrollable-tab">
                            <md-button class="add-btn" @click="openAddDialog('question')" :disabled="!circle.editing">
                                + Add {{ wizardLabels.question }}
                            </md-button>
                            <md-field v-for="(question, qIdx) in circle.questions" :key="`question-${qIdx}`">
                                <label class="md-list-item-text circle-label">
                                    Question {{ qIdx + 1 }}
                                </label>
                                <md-textarea v-model="circle.questions[qIdx]" name="wizard_questions"
                                    placeholder="Add question" :disabled="!circle.editing" :md-counter="limits.questions.max" :maxlength="limits.questions.max"/>
                                <md-button class="remove-btn" iconable @click="removeItem('question', qIdx)"
                                    :disabled="!circle.editing">
                                    <i class="fa fa-trash small-icon" aria-hidden="true"></i>
                                    <md-tooltip class="tooltip-text" md-direction="top">
                                        Remove Question
                                    </md-tooltip>
                                </md-button>
                            </md-field>
                        </div>
                    </div>
                </md-list-item>

                <!-- THANK YOU SECTION -->
                <md-list-item md-expand>
                    <div class="md-list-item-text">Thank you</div>
                    <div slot="md-expand" class="item-list-text">
                        <div class="scrollable-tab">
                            <md-button class="add-btn" @click="openAddDialog('thankyou')" :disabled="!circle.editing">
                                + Add {{ wizardLabels.thankyou }}
                            </md-button>
                            <md-field v-for="(thanks, tIdx) in circle.thankyou" :key="`thanks-${tIdx}`">
                                <label class="md-list-item-text circle-label">
                                    Thank you {{ tIdx + 1 }}
                                </label>
                                <md-textarea v-model="circle.thankyou[tIdx]" name="wizard_thankyou"
                                    placeholder="Add thank you" :disabled="!circle.editing" :md-counter="limits.thankyou.max" :maxlength="limits.thankyou.max"/>
                                <md-button class="remove-btn" iconable @click="removeItem('thankyou', tIdx)"
                                    :disabled="!circle.editing">
                                    <i class="fa fa-trash small-icon" aria-hidden="true"></i>
                                    <md-tooltip class="tooltip-text" md-direction="top">
                                        Remove Thank You
                                    </md-tooltip>
                                </md-button>
                            </md-field>
                        </div>
                    </div>
                </md-list-item>

                <!-- PROMPTS SECTION -->
                <md-list-item md-expand>
                    <div class="md-list-item-text">Prompts</div>
                    <div slot="md-expand" class="item-list-text">
                        <div class="scrollable-tab">
                            <md-button class="add-btn" @click="openAddDialog('prompt')" :disabled="!circle.editing">
                                + Add {{ wizardLabels.prompt }}
                            </md-button>
                            <md-field v-for="(prompt, pIdx) in circle.prompts" :key="`prompt-${pIdx}`">
                                <label class="md-list-item-text circle-label">
                                    Prompt {{ pIdx + 1 }}
                                </label>
                                <md-textarea v-model="circle.prompts[pIdx]" name="wizard_prompts"
                                    placeholder="Add prompt" :disabled="!circle.editing" :md-counter="limits.prompts.max" :maxlength="limits.prompts.max"/>
                                <md-button class="remove-btn" iconable @click="removeItem('prompt', pIdx)"
                                    :disabled="!circle.editing">
                                    <i class="fa fa-trash small-icon" aria-hidden="true"></i>
                                    <md-tooltip class="tooltip-text" md-direction="top">
                                        Remove Prompt
                                    </md-tooltip>
                                </md-button>
                            </md-field>
                        </div>
                    </div>
                </md-list-item>
            </md-list>
        </div>
        <!-- Component to add new items to the wizard -->
        <add-wizard-item ref="addWizardItem" :visible.sync="dialogVisible" :label="dialogLabel"
                :dialogType="dialogType" @confirm="confirmAdd" @cancel="dialogVisible = false" />
    </div>
</template>
<script>
import AddWizardItem from '@/components/AddWizardItem.vue';
import { sanitizeInput, sanitizeUrl, ensureHttpsScheme } from '@/utils/sanitizer';
export default {
    components: {
        AddWizardItem
    },
    props: {
        circle: Object,
        additionalParams: Array,
        idx: Number,
        actionPageOptions: Array,
        limits: Object
    },
    data() {
        return {
            dialogLabel: '',
            dialogVisible: false,
            dialogType: '',
            wizardLabels: {
                subject: 'Subject',
                intro: 'Introduction',
                question: 'Question',
                thankyou: 'Thank you',
                prompt: 'Prompt'
            }
        }
    },
    methods: {
        sanitizeUrl,
        ensureHttpsScheme,
        validate() {
            return this.$validator.validateAll();
        },
        confirmAdd(value) {
            const val = sanitizeInput(value);

            switch (this.dialogType) {
                case "subject":
                    this.circle.subjects.push(val);
                    break;
                case "intro":
                    this.circle.intros.push(val);
                    break;
                case "question":
                    this.circle.questions.push(val);
                    break;
                case "thankyou":
                    this.circle.thankyou.push(val);
                    break;
                case "prompt":
                    this.circle.prompts.push(val);
                    break;
            }
            this.dialogVisible = false;
        },
        /**
     * Remove an item from one of the wizard arrays by index.
     * @param {String} type - one of “subject” | “intro” | “question” | “thankyou” | “prompt”
     * @param {Number} index
     */
        removeItem(type, index) {
            switch (type) {
                case "subject":
                    this.circle.subjects.splice(index, 1);
                    break;
                case "intro":
                    this.circle.intros.splice(index, 1);
                    break;
                case "question":
                    this.circle.questions.splice(index, 1);
                    break;
                case "thankyou":
                    this.circle.thankyou.splice(index, 1);
                    break;
                case "prompt":
                    this.circle.prompts.splice(index, 1);
                    break;
            }
        },
        openAddDialog(type) {
            this.dialogType = type; // e.g. "subject"
            this.dialogLabel = this.wizardLabels[type]; // e.g. "Subject"
            this.dialogVisible = true;
        },
        // sanitize entry on-the-fly if needed
        sanitizeCustomizations(v) {
            return sanitizeInput(v);
        }
    }
}
</script>
<style>
/* -------------------------------------------------  */
/* GLOBAL (non‐scoped) media-query for responsivenes  */
/* ------------------------------------------------- */
@media (max-width: 768px) {
  /* Hide the desktop‐tabs on small screens */
  .desktop-tabs {
    display: none !important;
  }
  /* Show the accordion on small screens */
  .mobile-tabs {
    display: block !important;
  }
}

@media (min-width: 769px) {
  /* On large screens, show the tabs, hide the accordion */
  .desktop-tabs {
    display: block !important;
  }
  .mobile-tabs {
    display: none !important;
  }
}

/* ----------------------------------------------- */
/* SOME OPTIONAL TWEAKS to make the “accordion”    */
/* look a bit cleaner                              */
/* ----------------------------------------------- */
.mobile-tabs .md-list-item-text {
  font-weight: 600;
}


.mobile-tabs .md-list-item {
  margin-bottom: 8px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.mobile-tabs .md-list-item[md-expanded] {
  background-color: #fafafa;
}

.mobile-tabs .item-list-text {
    padding-inline: 16px;
}

.mobile-tabs .scrollable-tab {
  max-height: 300px;
  overflow-y: auto;
}

.md-field .md-chips {
    justify-content: flex-start !important;
}

.md-field.no-underline.md-theme-default:after {
    display: none !important;
}

.scrollable-tab {
    max-height: 500px;
    /* or whatever fixed height you need */
    overflow-y: auto;
}

.add-btn {
    margin-bottom: 8px;
}

.remove-btn {
    margin: 0;
    height: 20px;
    min-width: 30px;
    color: #D13525 !important;
}

.remove-btn[disabled] {
    color: rgba(16, 16, 16, 0.15) !important;
}

.tooltip-text {
    display: block !important;
    z-index: 10000;
}

.error-font {
    font-size: 1rem;
}

/* Force hardware compositing + crisper font rendering */
.circle-tab-root .md-chips,
.circle-tab-root .md-chips .md-chip {
  /* kill any transform on the container/layer */
  transform: none !important;
  will-change: auto !important;

  /* force a GPU layer but with sharp text */
  backface-visibility: hidden !important;
  -webkit-font-smoothing: antialiased !important;
  -moz-osx-font-smoothing: grayscale !important;
}

.mobile-tabs .md-list-item[md-expanded] .scrollable-tab,
.mobile-tabs .md-list-item[md-expanded] .md-list-item-container {
  transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

</style>


// WEBPACK FOOTER //
// src/components/CircleTabs.vue